import React, { useState, useEffect, useRef } from 'react';
import { listPhotos, uploadPhotos } from '../api/api';
import ThumbnailGallery from './ThumbnailGallery';
import PhotoEditor from './PhotoEditor';

const FoldersView = ({ onBack }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolderData, setSelectedFolderData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedNewFiles, setSelectedNewFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    // Get all session IDs from localStorage history
    const sessions = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('session_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          
          // Get photo count for this session
          try {
            const response = await listPhotos(data.sessionId);
            data.photoCount = response.photos?.length || 0;
          } catch (error) {
            console.error('Error loading photo count:', error);
            data.photoCount = 0;
          }
          
          sessions.push(data);
        } catch (e) {
          console.error('Error parsing session:', e);
        }
      }
    }
    setFolders(sessions);
  };

  const loadFolderPhotos = async (sessionId) => {
    setLoading(true);
    try {
      const response = await listPhotos(sessionId);
      setPhotos(response.photos || []);
      setSelectedFolder(sessionId);
      
      // Load folder data
      const folderData = localStorage.getItem(`session_${sessionId}`);
      if (folderData) {
        const data = JSON.parse(folderData);
        setSelectedFolderData(data);
        setNewName(data.name || '');
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      alert('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleRename = () => {
    if (!newName.trim()) {
      alert('Please enter a name');
      return;
    }
    
    const folderData = JSON.parse(localStorage.getItem(`session_${selectedFolder}`));
    folderData.name = newName.trim();
    localStorage.setItem(`session_${selectedFolder}`, JSON.stringify(folderData));
    
    setSelectedFolderData(folderData);
    setIsRenaming(false);
    alert('✅ Folder renamed successfully!');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setSelectedNewFiles(files);
  };

  const handleUploadMore = async () => {
    if (selectedNewFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    let uploadedCount = 0;

    try {
      // Upload files in parallel batches (NO COMPRESSION for full quality)
      const BATCH_SIZE = 5; // Upload 5 files simultaneously
      const batches = [];
      
      for (let i = 0; i < selectedNewFiles.length; i += BATCH_SIZE) {
        batches.push(selectedNewFiles.slice(i, i + BATCH_SIZE));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Upload all files in this batch simultaneously with retry logic
        await Promise.all(
          batch.map(async (file) => {
            let retries = 3;
            let uploaded = false;
            
            while (retries > 0 && !uploaded) {
              try {
                await uploadPhotos(selectedFolder, [file], () => {});
                uploaded = true;
                uploadedCount++;
                const progress = Math.round((uploadedCount / selectedNewFiles.length) * 100);
                setUploadProgress(progress);
              } catch (error) {
                retries--;
                console.error(`Upload failed for ${file.name}, retries left: ${retries}`, error);
                if (retries > 0) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                } else {
                  throw new Error(`Failed to upload ${file.name} after 3 attempts`);
                }
              }
            }
          })
        );
      }

      // Refresh photo list
      const response = await listPhotos(selectedFolder);
      setPhotos(response.photos || []);
      
      setSelectedNewFiles([]);
      setIsUploading(false);
      alert(`✅ ${selectedNewFiles.length} photo(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    try {
      const { deletePhoto } = await import('../api/api');
      await deletePhoto(photo.id);
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setShowEditor(false); // Close editor after delete
      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
      return false;
    }
  };
  
  // Expose delete handler globally for PhotoEditor
  useEffect(() => {
    window.handlePhotoDelete = handleDeletePhoto;
    window.closeEditor = () => setShowEditor(false);
    return () => {
      delete window.handlePhotoDelete;
      delete window.closeEditor;
    };
  }, [photos, selectedFolder]);

  const handleEditPhoto = (photo) => {
    setCurrentPhoto(photo);
    setShowEditor(true);
  };

  const handlePhotoClick = (photo) => {
    setCurrentPhoto(photo);
    setShowEditor(true);
  };

  const handleSaveEdit = async (photo, adjustments, adjustedImageBlob) => {
    try {
      console.log('Saving photo with adjustments:', { photo, adjustments });
      
      // Upload the edited blob to replace the original
      const { updatePhoto } = await import('../api/api');
      const response = await updatePhoto(photo.id, adjustedImageBlob);
      
      // Refresh photos to show updated version
      const { listPhotos } = await import('../api/api');
      const photosResponse = await listPhotos(selectedFolder);
      setPhotos(photosResponse.photos || []);
      
      console.log('✅ Photo updated successfully:', response);
      // Don't close editor here, let PhotoEditor handle it
    } catch (error) {
      console.error('Error saving photo adjustments:', error);
      throw error;
    }
  };

  if (selectedFolder) {
    return (
      <div className="min-h-screen p-2 sm:p-3 md:p-4" style={{background: 'linear-gradient(to bottom right, #FFE5F1, #E27C7A)'}}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6 mb-3 sm:mb-4 md:mb-6">
            <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setSelectedFolder(null);
                    setIsRenaming(false);
                  }}
                  className="text-xs sm:text-sm md:text-base text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 sm:gap-2"
                >
                  ← Back to Folders
                </button>
                <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800">
                  {photos.length} Photos
                </h2>
              </div>

              {/* Folder Name and Actions */}
              <div className="flex items-center gap-4 flex-wrap">
                {isRenaming ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRename();
                        } else if (e.key === 'Escape') {
                          setIsRenaming(false);
                        }
                      }}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Folder name"
                      autoFocus
                    />
                    <button
                      onClick={handleRename}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setIsRenaming(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap flex-1">
                    <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                      {selectedFolderData?.name || 'Unnamed Folder'}
                    </h3>
                    <button
                      onClick={() => {
                        setNewName(selectedFolderData?.name || '');
                        setIsRenaming(true);
                      }}
                      className="px-2 py-1 md:px-3 md:py-1 bg-gray-200 hover:bg-gray-300 rounded-lg text-xs md:text-sm font-medium"
                    >
                      ✏️ Rename
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="px-3 py-1 md:px-4 md:py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs md:text-base font-semibold disabled:opacity-50"
                    >
                      + Add Photos
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {isUploading && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>

          {/* Selected New Photos Preview */}
          {selectedNewFiles.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm md:text-base font-semibold text-gray-800">
                  📷 {selectedNewFiles.length} New Photos Selected
                </h4>
                <button
                  onClick={() => setSelectedNewFiles([])}
                  className="text-xs md:text-sm text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3 max-h-40 overflow-y-auto">
                {selectedNewFiles.map((file, index) => (
                  <div key={index} className="relative aspect-square bg-white rounded overflow-hidden shadow">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={handleUploadMore}
                disabled={isUploading}
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : `Upload ${selectedNewFiles.length} Photo(s)`}
              </button>
            </div>
          )}

          {/* Photos Gallery */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-6">
              <ThumbnailGallery
                photos={photos}
                onDelete={handleDeletePhoto}
                onEdit={handleEditPhoto}
                onPhotoClick={handlePhotoClick}
              />
            </div>
          )}

          {/* Photo Editor Modal */}
          {showEditor && currentPhoto && (
            <PhotoEditor
              photo={currentPhoto}
              photos={photos}
              onClose={() => setShowEditor(false)}
              onSave={handleSaveEdit}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-2 sm:p-3 md:p-4" style={{background: 'linear-gradient(to bottom right, #FFE5F1, #E27C7A)'}}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6 mb-3 sm:mb-4 md:mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">📁 All Folders</h1>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 md:mt-1">{folders.length} session(s)</p>
            </div>
            <button
              onClick={onBack}
              className="px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors text-xs sm:text-sm md:text-base"
            >
              Back to Home
            </button>
          </div>
        </div>

        {/* Folders Grid */}
        {folders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 md:p-12 text-center">
            <span className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-3 md:mb-4 block">📂</span>
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No folders yet</h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-500">Upload some photos to create your first folder</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {folders.map((folder) => (
              <div
                key={folder.sessionId}
                onClick={() => loadFolderPhotos(folder.sessionId)}
                className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6 cursor-pointer hover:shadow-xl transition-shadow transform hover:scale-105"
              >
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                  <span className="text-3xl sm:text-4xl md:text-5xl">📁</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-800 text-sm sm:text-base md:text-lg truncate">
                      {folder.name || 'Unnamed Session'}
                    </h3>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">
                      {new Date(folder.createdAt).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                      <span className="text-[10px] sm:text-xs md:text-sm font-semibold text-primary-600">
                        📷 {folder.photoCount || 0} photos
                      </span>
                      <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-400">
                        {folder.formData?.quantity || 0} ordered
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FoldersView;

